import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { tagsApi } from "@/api/tags";
import type { MediaTagKind, TagRow } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import { TagChip } from "@/pages/settings/Tags";

/**
 * Inline editor for an entity's attached tag set. Renders the current
 * tags as chips, with an "Edit" affordance that pops a dropdown of
 * every tag in the registry. Used on the Indexers settings page and
 * each per-media detail page.
 *
 * The component is generic over indexer-vs-media via the `target`
 * discriminator — same UI, different fetch / replace endpoint pair.
 *
 * Both the read and the write go through {@link tagsApi}; the parent
 * just needs to mount this and forget about it. We bust two query
 * keys on save so any other surface that renders the same set
 * re-fetches: the entity-level fetch and the global tag list.
 */
type TagTarget =
  | { kind: "indexer"; id: number }
  | { kind: "media"; mediaKind: MediaTagKind; id: number };

interface Props {
  target: TagTarget;
  /** Hide the "Edit" button — useful for read-only contexts. */
  readOnly?: boolean;
  /** Treat the empty state ("No tags") as worth showing. Disabled
   *  in dense lists where the row is implicit. */
  showEmpty?: boolean;
}

export function TagPills({ target, readOnly, showEmpty }: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);

  const queryKey = targetQueryKey(target);
  const { data: attached, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      target.kind === "indexer"
        ? tagsApi.forIndexer(target.id)
        : tagsApi.forMedia(target.mediaKind, target.id),
  });

  const { data: allTags } = useQuery({
    queryKey: ["tags"],
    queryFn: tagsApi.list,
    enabled: editing,
  });

  const setMut = useMutation({
    mutationFn: (tag_ids: number[]) =>
      target.kind === "indexer"
        ? tagsApi.setForIndexer(target.id, tag_ids)
        : tagsApi.setForMedia(target.mediaKind, target.id, tag_ids),
    onSuccess: () => {
      toast("Tags updated", "success");
      qc.invalidateQueries({ queryKey });
      setEditing(false);
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const attachedIds = useMemo(
    () => new Set((attached ?? []).map((t) => t.id)),
    [attached],
  );

  if (isLoading && !showEmpty) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {(attached ?? []).map((t) => (
        <TagChip key={t.id} tag={t} />
      ))}
      {(!attached || attached.length === 0) && showEmpty && (
        <span className="text-xs italic text-foreground/40">No tags</span>
      )}
      {!readOnly && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setEditing((v) => !v)}
        >
          {editing ? "Cancel" : "Edit tags"}
        </Button>
      )}

      {editing && allTags && (
        <TagEditor
          allTags={allTags}
          initialAttached={attachedIds}
          saving={setMut.isPending}
          onSave={(ids) => setMut.mutate(ids)}
        />
      )}
    </div>
  );
}

function targetQueryKey(target: TagTarget): unknown[] {
  if (target.kind === "indexer") {
    return ["indexer-tags", target.id];
  }
  return ["media-tags", target.mediaKind, target.id];
}

/**
 * Tag-set editor — one toggle per tag in the registry. The set is
 * applied with set-semantics (`PUT`) so the user can both add and
 * remove in one round-trip.
 */
function TagEditor({
  allTags,
  initialAttached,
  saving,
  onSave,
}: {
  allTags: TagRow[];
  initialAttached: Set<number>;
  saving: boolean;
  onSave: (ids: number[]) => void;
}) {
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(initialAttached),
  );

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (allTags.length === 0) {
    return (
      <div className="ml-2 rounded border border-line bg-surface px-3 py-2 text-xs text-foreground/60">
        No tags configured. Create one on Settings → Tags first.
      </div>
    );
  }

  return (
    <div className="ml-2 flex flex-wrap items-center gap-1.5 rounded border border-line bg-surface px-3 py-2">
      {allTags.map((t) => {
        const on = selected.has(t.id);
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => toggle(t.id)}
            className={`rounded px-2 py-0.5 text-xs transition ${
              on
                ? "border border-accent text-foreground"
                : "border border-line text-foreground/50 hover:text-foreground/80"
            }`}
            style={
              on && t.color ? { background: t.color, color: "#0b0f17" } : undefined
            }
          >
            {t.name}
          </button>
        );
      })}
      <Button
        size="sm"
        loading={saving}
        onClick={() => onSave(Array.from(selected).sort((a, b) => a - b))}
      >
        Save
      </Button>
    </div>
  );
}
